// Authentication Context Provider
// Manages user authentication state and provides auth methods to components

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { logger } from '../utils/logger';

export interface User {
  id: string;
  username: string;
  avatar: string;
  provider: 'github' | 'discord';
  is_moderator: boolean;
  is_admin: boolean;
  contributions?: {
    examples: number;
    notes: number;
    issues: number;
  };
  created_at: string;
  last_login: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (provider: 'github' | 'discord') => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  getAuthToken: () => string | null;
  getAuthHeader: () => Record<string, string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check for auth on mount and after OAuth redirect
  useEffect(() => {
    // Check URL hash for OAuth success/error
    const hash = window.location.hash;
    if (hash.includes('auth_success')) {
      logger.log('🔐 Auth success, fetching profile from cookie...');
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      fetchUserProfile();
    } else if (hash.includes('auth_error')) {
      logger.error('🔐 Auth error:', hash);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      setLoading(false);
    } else {
      // Try to fetch profile from cookie
      fetchUserProfile();
    }
  }, []);

  const fetchUserProfile = async () => {
    try {
      // Use httpOnly cookie authentication
      const response = await fetch('/api/auth/me', {
        credentials: 'include', // Include cookies
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        logger.log('✅ User profile loaded from cookie');
      } else if (response.status === 401) {
        // Not authenticated or token expired
        logger.log('🔐 No valid authentication');
        setUser(null);
      }
    } catch (error) {
      logger.error('Failed to fetch user profile:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (provider: 'github' | 'discord') => {
    // Redirect to OAuth endpoint
    window.location.href = `/api/auth/${provider}`;
  };

  const logout = async () => {
    // Call logout endpoint to clear cookie
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      logger.error('Logout error:', error);
    }
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUserProfile();
  };

  // Auth helper methods for cookie-based authentication
  const getAuthToken = () => {
    // Cookies are httpOnly, so we can't access them from JavaScript
    // This method is kept for API compatibility but returns null
    return null;
  };

  const getAuthHeader = () => {
    // With httpOnly cookies, credentials are sent automatically
    // No need to manually add Authorization header
    return {};
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    logout,
    refreshUser,
    getAuthToken,
    getAuthHeader
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
